import React from 'react';

import {
  IconBarrierBlock,
  IconBuildingBroadcastTower,
  IconBulb,
  IconCarGarage,
  IconChartBar,
  IconChartDots2,
  IconCurrentLocation,
  IconDeviceMobileMessage,
  IconEye,
  IconGavel,
  IconHandClick,
  IconHeartRateMonitor,
  IconKey,
  IconMapSearch,
  IconReportMoney,
  IconRocket,
  IconRosetteDiscountCheck,
  IconSlideshow,
  IconTargetArrow,
  IconTemplate,
  IconTimeline,
  IconUsersGroup,
  IconVideo,
} from '@tabler/icons-react';

import { useAtom } from 'jotai';

import { PopoeverMenuKeys } from 'components/SideNavbar/SideNavbar';
import { FeatureFlag } from 'types/FeatureFlags';
import Chip from '@lotlinx-aws/sparky-component-library/Sparky/Chip';
import { marketInsightVersionStatusAtom } from 'store/marketInsightVersionStatusAtom';
import styles from './SidebarPopover.module.scss';
import useFeatureFlag from 'hooks/useFeatureFlag';

export interface SidebarPopoverLink {
  title: string;
  icon: React.ReactElement;
  location: string;
  flag?: FeatureFlag;
  isVmbiSubscriptionRestricted?: boolean;
  suffixIcon?: React.ReactElement;
  suffixIconLocation?: string;
}

export const POPOVER_MENU_DEFS: Record<
  Exclude<PopoeverMenuKeys, 'more'>,
  Record<string, SidebarPopoverLink[]>
> = {
  analytics: {
    Analytics: [],
    Risk: [
      {
        title: 'Risk Overview',
        icon: <IconBarrierBlock size={20} color={'var(--icon-action)'} />,
        location: '/analytics/risk_overview',
        flag: FeatureFlag.ENABLE_RISK_OVERVIEW,
      },
      {
        title: 'Segment Analysis',
        icon: <IconCarGarage size={20} color={'var(--icon-action)'} />,
        location: '/analytics/risk_analysis',
        flag: FeatureFlag.ENABLE_RISK_ANALYSIS,
      },
      {
        title: 'VIN Strategy Report',
        icon: <IconTargetArrow size={20} color={'var(--icon-action)'} />,
        location: '/analytics/vsr',
        flag: FeatureFlag.ENABLE_VSR,
        isVmbiSubscriptionRestricted: true,
      },
    ],
    Market: [
      {
        title: 'Market Insights',
        icon: <IconBulb size={20} color={'var(--icon-action)'} />,
        location: '/analytics/market_insights',
        flag: FeatureFlag.ENABLE_MARKET_INSIGHTS_PAGE,
        suffixIconLocation: '/analytics/market_insights_v2',
      },
      {
        title: 'Market Opportunity',
        icon: <IconMapSearch size={20} color={'var(--icon-action)'} />,
        location: '/analytics/market_opportunity',
        flag: FeatureFlag.ENABLE_MARKET_OPPORTUNITY,
        isVmbiSubscriptionRestricted: true,
      },
      {
        title: 'Before & After',
        icon: <IconTimeline size={20} color={'var(--icon-action)'} />,
        location: '/analytics/before_after',
        flag: FeatureFlag.ENABLE_BEFORE_AND_AFTER,
        isVmbiSubscriptionRestricted: true,
      },
    ],
  },
  inventory: {
    Inventory: [
      {
        title: 'Live Inventory',
        icon: <IconRosetteDiscountCheck size={20} color={'var(--icon-action)'} />,
        location: '/inventory/live',
      },
      {
        title: 'Sold Inventory',
        icon: <IconGavel size={20} color={'var(--icon-action)'} />,
        location: '/inventory/sold',
      },
      {
        title: 'VIN View Optimizer®',
        icon: <IconEye size={20} color={'var(--icon-action)'} />,
        location: 'vvo',
        flag: FeatureFlag.ENABLE_VVO,
      },
      {
        title: 'Inventory Health',
        icon: <IconHeartRateMonitor size={20} color={'var(--icon-action)'} />,
        location: 'inventory-health',
        flag: FeatureFlag.ENABLE_INVENTORY_HEALTH,
      },
    ],
  },
  campaigns: {
    Campaigns: [
      {
        title: 'Performance Results',
        icon: <IconRocket size={20} color={'var(--icon-action)'} />,
        location: '/campaigns/performance_results',
        flag: FeatureFlag.ENABLE_PERFORMANCE_RESULTS,
      },
      {
        title: 'Overview',
        icon: <IconChartDots2 size={20} color={'var(--icon-action)'} />,
        location: '/campaigns/overview',
        flag: FeatureFlag.ENABLE_DEALER_LANDING_PAGE,
      },
      {
        title: 'Detailed Overview',
        icon: <IconChartDots2 size={20} color={'var(--icon-action)'} />,
        location: '/campaigns/performance',
        flag: FeatureFlag.ENABLE_CAMPAIGN_OVERVIEW,
      },
      {
        title: 'Conversions',
        icon: <IconDeviceMobileMessage size={20} color={'var(--icon-action)'} />,
        location: '/campaigns/conversions',
        flag: FeatureFlag.ENABLE_CONVERSION_REPORT,
      },
      {
        title: 'ROI Report',
        icon: <IconReportMoney size={20} color={'var(--icon-action)'} />,
        location: '/campaigns/roi_report',
        flag: FeatureFlag.ENABLE_ROI_REPORT,
      },
    ],
  },
  leadRevival: {
    Shoppers: [
      {
        title: 'Leads',
        icon: <IconUsersGroup size={20} color={'var(--icon-action)'} />,
        location: '/leadRevival',
        flag: FeatureFlag.ENABLE_IDP_ORIGINAL,
      },
    ],
  },
  dealerManagement: {
    'Dealer Managment': [],
    Settings: [
      {
        title: 'Local Market',
        icon: <IconCurrentLocation size={20} color={'var(--icon-action)'} />,
        location: '/settings/local_market',
        flag: FeatureFlag.ENABLE_LOCAL_MARKET_SETTINGS,
      },
      {
        title: 'Geo Configuration',
        icon: <IconBuildingBroadcastTower size={20} color={'var(--icon-action)'} />,
        location: '/settings/geo_config',
        flag: FeatureFlag.ENABLE_GEO_CONFIG_SETTING,
      },
      {
        title: 'Showroom',
        icon: <IconTemplate size={20} color={'var(--icon-action)'} />,
        location: '/settings/showroom',
        flag: FeatureFlag.ENABLE_SHOWROOM,
      },
      {
        title: 'LX Video',
        icon: <IconVideo size={20} color={'var(--icon-action)'} />,
        location: '/settings/lx_video_config',
        flag: FeatureFlag.ENABLE_LX_VIDEO_CONFIG_SETTING,
      },
      {
        title: 'Photo AI',
        icon: <IconSlideshow size={20} color={'var(--icon-action)'} />,
        location: '/settings/photo_ai_config',
        flag: FeatureFlag.ENABLE_PHOTO_AI_SETTING,
      },
      {
        title: 'VMX Access',
        icon: <IconKey size={20} color={'var(--icon-action)'} />,
        location: '/settings/vmx_config',
        flag: FeatureFlag.ENABLE_VMX_ACCESS,
      },
    ],
    Admin: [
      {
        title: 'Account Activity',
        icon: <IconHandClick size={20} color={'var(--icon-action)'} />,
        location: '/admin/activity',
        flag: FeatureFlag.ENABLE_ADMIN_ACTIVITY,
      },

      {
        title: 'Users',
        icon: <IconUsersGroup size={20} color={'var(--icon-action)'} />,
        location: '/admin/users',
        flag: FeatureFlag.ENABLE_USER_LOGIN,
      },
    ],
  },
};
